/**
 * Feature flags configuration
 */

import { useAuthStore, Roles } from "../stores/authStore.ts";

/**
 * Check if billing feature is enabled
 * Uses kindergarten settings from auth store (backend-driven)
 * @returns {boolean} True if billing feature is enabled
 */
/**
 * Financial Management module (finance) — per-school, ROOT_ADMIN-enabled.
 * Function form ONLY (evaluated per call): the module is toggled at runtime
 * via PUT /api/v1/finance/settings and the reactive flag arrives through
 * authStore.kindergartenSettings. Do NOT add this to the load-time-stale
 * `FeatureFlags` const below.
 */
export const isFinanceEnabled = (): boolean => {
  const state = useAuthStore.getState();

  // Root admins have no school of their own; the finance backend gate always
  // lets them through (they pick a school scope inside the finance views).
  if (state.user?.role === Roles.ROOT_ADMIN) return true;

  const settings = state.kindergartenSettings;
  if (!settings) return false;
  return settings.finance_enabled === true;
};

/**
 * Bus Fees module — per-school, ROOT_ADMIN-enabled, dashboard-only. A fee
 * list derived from each child's bus arrangement, for schools that keep
 * their books in their own accounting software. Standalone from billing and
 * finance: its own switch (`bus_fees_enabled` on the login payload), its own
 * sidebar group, its own endpoints. Function form only, like finance — the
 * flag is patched into authStore when a root admin flips it.
 */
export const isBusFeesEnabled = (): boolean => {
  const state = useAuthStore.getState();
  if (state.user?.role === Roles.ROOT_ADMIN) return true;
  const settings = state.kindergartenSettings;
  if (!settings) return false;
  return settings.bus_fees_enabled === true;
};

/**
 * Riders who track their own bus «الركاب» — per-school, ROOT_ADMIN-enabled
 * (`riders_enabled` on the login payload, from `general_settings.riders`).
 * It gates *adding* a rider and nothing else: listing, editing and removing
 * stay open, and a rider already on a bus keeps riding. Function form, like
 * Bus Fees — the flag is patched into authStore on a settings save.
 *
 * True for a root admin, whatever the school: a screen that needs the school's
 * own answer reads it from `useSchoolIdentity().ridersEnabled`.
 */
export const isRidersEnabled = (): boolean => {
  const state = useAuthStore.getState();
  if (state.user?.role === Roles.ROOT_ADMIN) return true;
  const settings = state.kindergartenSettings;
  if (!settings) return false;
  return settings.riders_enabled === true;
};

/**
 * Merging two journeys into one — per-school, ROOT_ADMIN-enabled
 * (`journey_merge_enabled` on the login payload, from
 * `general_settings.journey_merge`). A school with a new, bigger bus folds a
 * second route into the first. It gates *merging* and nothing else: a merge
 * already made stays made. Function form, like Riders — the flag is patched
 * into authStore on a settings save.
 *
 * True for a root admin, whatever the school: BusWay lets root merge in any
 * school, switched on or not.
 */
export const isJourneyMergeEnabled = (): boolean => {
  const state = useAuthStore.getState();
  if (state.user?.role === Roles.ROOT_ADMIN) return true;
  const settings = state.kindergartenSettings;
  if (!settings) return false;
  return settings.journey_merge_enabled === true;
};

/**
 * Polls «الاستطلاعات» — the school admin writes the school's polls while its
 * Polls service is on (`polls_enabled` on the login payload and the public
 * settings, from `general_settings.polls`: Wosool opens it on the Services
 * page, the admin switches it). Function form, like Riders — the session's
 * switches are kept current by `useSchoolSwitchesRefresh`.
 *
 * True for a root admin, whatever the school: root reads any school's polls
 * (and writes Wosool's, which no school switch stops).
 */
export const isPollsEnabled = (): boolean => {
  const state = useAuthStore.getState();
  if (state.user?.role === Roles.ROOT_ADMIN) return true;
  const settings = state.kindergartenSettings;
  if (!settings) return false;
  return settings.polls_enabled === true;
};

export const isBillingEnabled = (): boolean => {
  const state = useAuthStore.getState();

  // Root admins have no school of their own, so there is no per-school
  // billing_enabled flag to consult — billing is always available and the
  // school scope is chosen inside the billing views.
  if (state.user?.role === Roles.ROOT_ADMIN) return true;

  const settings = state.kindergartenSettings;

  // If no settings (e.g., not logged in), default to false
  if (!settings) return false;

  return settings.billing_enabled === true;
};

export const isEnhancedNotificationsEnabled = (): boolean => {
  // Temporarily enable for testing - TODO: Set REACT_APP_ENHANCED_NOTIFICATIONS=true in .env.local
  return process.env.REACT_APP_ENHANCED_NOTIFICATIONS === "true" || true;
};

export const isNotificationWebSocketEnabled = (): boolean => {
  return process.env.REACT_APP_NOTIFICATION_WEBSOCKETS === "true";
};

export const isTemplateManagementEnabled = (): boolean => {
  return process.env.REACT_APP_TEMPLATE_MANAGEMENT === "true";
};

export const isDeviceManagementEnabled = (): boolean => {
  return process.env.REACT_APP_DEVICE_MANAGEMENT === "true";
};

export const isNotificationAnalyticsEnabled = (): boolean => {
  return process.env.REACT_APP_NOTIFICATION_ANALYTICS === "true";
};

/**
 * Check if driver and parent access to web dashboard is enabled
 * Uses environment variable REACT_APP_ENABLE_DRIVER_PARENT_ACCESS
 * @returns {boolean} True if driver/parent access is enabled
 */
export const isDriverParentAccessEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_DRIVER_PARENT_ACCESS === "true";
};

/**
 * Check if recurring events feature is enabled
 * Currently disabled due to issues with calendar display
 * To enable: Set REACT_APP_ENABLE_RECURRING_EVENTS=true in .env
 * @returns {boolean} True if recurring events feature is enabled
 */
export const isRecurringEventsEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_RECURRING_EVENTS === "true";
};

/**
 * Feature flags object for easy access
 */
export const FeatureFlags = {
  BILLING: isBillingEnabled(),
  DRIVER_PARENT_ACCESS: isDriverParentAccessEnabled(),
  RECURRING_EVENTS: isRecurringEventsEnabled(),
} as const;
